import {
  Box,
  Button,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isUploadDestinationStepValid,
  isUploadFilesStepValid,
  isUploadSubmitReady,
} from "../../utils/documentUploadValidation";
import { ContentTypeSegmentedControl } from "../shared/ContentTypeSegmentedControl";
import {
  DocumentUploadOptions,
  type DocumentUploadOptionsProps,
} from "./DocumentUploadOptions";
import {
  DocumentUploadSection,
  type DocumentUploadSectionProps,
} from "./DocumentUploadSection";
import type { UploadMediaMode } from "../../utils/uploadFileUtils";

const UPLOAD_STEP_COUNT = 3;

export type DocumentUploadPanelProps = DocumentUploadSectionProps &
  Omit<DocumentUploadOptionsProps, "section" | "submitDisabled"> & {
    onSubmit: (e: React.FormEvent) => void;
    uploadMediaMode: UploadMediaMode;
    onUploadMediaModeChange: (mode: UploadMediaMode) => void;
  };

export function DocumentUploadPanel({
  onSubmit,
  files,
  busy,
  busyLabel,
  uploadMediaMode,
  onUploadMediaModeChange,
  ...rest
}: DocumentUploadPanelProps) {
  const filesCount = files.length;
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const sectionProps: DocumentUploadSectionProps = {
    uploadMediaMode,
    files,
    busy,
    fileInputRef: rest.fileInputRef,
    folderInputRef: rest.folderInputRef,
    onDragOver: rest.onDragOver,
    onDrop: rest.onDrop,
    onFileChange: rest.onFileChange,
    onFolderChange: rest.onFolderChange,
    onRemoveFile: rest.onRemoveFile,
    onSelectFolder: rest.onSelectFolder,
  };

  const optionsProps: Omit<
    DocumentUploadOptionsProps,
    "section" | "submitDisabled" | "filesCount"
  > = {
    busy,
    busyLabel,
    apiDatasets: rest.apiDatasets,
    datasetTargetMode: rest.datasetTargetMode,
    onDatasetTargetModeChange: rest.onDatasetTargetModeChange,
    datasetSelection: rest.datasetSelection,
    onDatasetSelectionChange: rest.onDatasetSelectionChange,
    newDatasetName: rest.newDatasetName,
    onNewDatasetNameChange: rest.onNewDatasetNameChange,
    newDatasetDescription: rest.newDatasetDescription,
    onNewDatasetDescriptionChange: rest.onNewDatasetDescriptionChange,
    activeDataset: rest.activeDataset,
    onRenameDataset: rest.onRenameDataset,
    uploadRoleAccess: rest.uploadRoleAccess,
    onUploadRoleAccessChange: rest.onUploadRoleAccessChange,
    roleOptions: rest.roleOptions,
    uploadCategory: rest.uploadCategory,
    onUploadCategoryChange: rest.onUploadCategoryChange,
  };

  const stepLabels = useMemo(
    () => [
      t("opva.dmUploadStepShortFiles", { defaultValue: "Files" }),
      t("opva.dmUploadStepShortStorage", { defaultValue: "Storage" }),
      t("opva.dmUploadStepShortCategory", { defaultValue: "Category" }),
    ],
    [t]
  );

  const filesStepValid = isUploadFilesStepValid(filesCount);
  const destinationStepValid = isUploadDestinationStepValid(
    optionsProps.datasetTargetMode,
    optionsProps.datasetSelection,
    optionsProps.apiDatasets,
    optionsProps.newDatasetName,
    optionsProps.uploadRoleAccess
  );
  const submitReady = isUploadSubmitReady(
    filesCount,
    optionsProps.uploadCategory,
    optionsProps.datasetTargetMode,
    optionsProps.datasetSelection,
    optionsProps.apiDatasets,
    optionsProps.newDatasetName,
    optionsProps.uploadRoleAccess,
    busy
  );

  useEffect(() => {
    if (filesCount === 0 && activeStep > 0) {
      setActiveStep(0);
    }
  }, [filesCount, activeStep]);

  useEffect(() => {
    setActiveStep(0);
  }, [uploadMediaMode]);

  const handleNext = () => {
    if (activeStep >= UPLOAD_STEP_COUNT - 1) return;
    if (activeStep === 0 && !filesStepValid) return;
    if (activeStep === 1 && !destinationStepValid) return;
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    if (busy || activeStep <= 0) return;
    setActiveStep((s) => s - 1);
  };

  const nextDisabled =
    busy ||
    (activeStep === 0 && !filesStepValid) ||
    (activeStep === 1 && !destinationStepValid);

  const handleMediaModeChange = (value: UploadMediaMode) => {
    if (busy) return;
    onUploadMediaModeChange(value);
  };

  return (
    <Box component="form" onSubmit={onSubmit} className="dm-upload-panel">
      <Box sx={{ mb: 2, minHeight: 40, display: "flex", alignItems: "center" }}>
        <ContentTypeSegmentedControl
          value={uploadMediaMode}
          onChange={handleMediaModeChange}
          disabled={busy}
          aria-label={t("opva.dmUploadMediaTabsAria", {
            defaultValue: "Upload content type",
          })}
        />
      </Box>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        className="dm-upload-stepper"
        sx={{ mb: { xs: 2, md: 2.5 } }}
      >
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep > 0 && filesCount > 0 ? (
        <DocumentUploadSection
          {...sectionProps}
          variant="bar"
          onEditFiles={() => setActiveStep(0)}
        />
      ) : null}

      {activeStep === 0 ? (
        <Box className="dm-upload-step0">
          <DocumentUploadSection {...sectionProps} variant="full" />
          {filesCount > 0 ? (
            <Box sx={{ mt: 2 }}>
              <DocumentUploadSection {...sectionProps} variant="summary" removable />
            </Box>
          ) : (
            <Box
              className="dm-upload-step-panel dm-upload-step-panel--hint"
              sx={{ mt: 2, minHeight: 52 }}
            >
              <Typography variant="body2" color="text.secondary">
                {uploadMediaMode === "video"
                  ? t("opva.dmUploadStepVideosHint", {
                      defaultValue:
                        "Add one or more video files, then continue to set storage and access.",
                    })
                  : t("opva.dmUploadStepFilesHint", {
                      defaultValue:
                        "Add one or more files or a folder, then continue to set storage and access.",
                    })}
              </Typography>
            </Box>
          )}
        </Box>
      ) : null}

      {activeStep === 1 ? (
        <DocumentUploadOptions {...optionsProps} section="destination" />
      ) : null}

      {activeStep === 2 ? (
        <DocumentUploadOptions {...optionsProps} section="category" />
      ) : null}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        className="dm-upload-nav"
        sx={{ mt: 3 }}
      >
        <Button
          type="button"
          variant="outlined"
          disabled={busy || activeStep === 0}
          onClick={handleBack}
          sx={{ textTransform: "none" }}
        >
          {t("opva.back", { defaultValue: "Back" })}
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          {activeStep < UPLOAD_STEP_COUNT - 1 ? (
            <Button
              type="button"
              variant="contained"
              disabled={nextDisabled}
              onClick={handleNext}
              sx={{ textTransform: "none" }}
            >
              {t("opva.next", { defaultValue: "Next" })}
            </Button>
          ) : (
            <DocumentUploadOptions
              {...optionsProps}
              section="submit"
              filesCount={filesCount}
              submitDisabled={!submitReady}
            />
          )}
        </Stack>
      </Stack>

    </Box>
  );
}
